import React, { useMemo } from 'react';
import { Platform } from 'react-native';
import { TextInput } from 'react-native-paper';
import { C, FONT, R } from '../../theme/tokens';
import { sanitizeNumberInput, SanitizeNumberOptions } from '../../utils/numberInput';
import { useCalcShellCommit } from './CalcShell';

export type NumericFieldProps = {
  label: string;
  value: string;
  onChangeText: (next: string) => void;
  placeholder?: string;
  mode?: 'outlined' | 'flat';
  disabled?: boolean;
  clearable?: boolean;
  readOnly?: boolean;
  onSubmitEditing?: () => void;
  /** Unit tick shown in the trailing corner (e.g. "CFM", "ft/min"). */
  rightText?: string;
} & SanitizeNumberOptions;

export function NumericField({
  label,
  value,
  onChangeText,
  placeholder,
  mode = 'outlined',
  disabled,
  clearable = true,
  readOnly,
  allowNegative,
  maxDecimals,
  onSubmitEditing,
  rightText,
}: NumericFieldProps) {
  const commit = useCalcShellCommit();
  const handleSubmit = onSubmitEditing
    ? () => {
        onSubmitEditing();
        commit();
      }
    : undefined;

  const keyboardType = useMemo(
    () => (Platform.OS === 'web' ? 'default' : 'decimal-pad'),
    [],
  );

  const handleChange = (raw: string) => {
    onChangeText(sanitizeNumberInput(raw, { allowNegative, maxDecimals }));
  };

  const right = useMemo(() => {
    if (rightText) {
      return (
        <TextInput.Affix
          text={rightText}
          textStyle={{ fontFamily: FONT.mono, fontSize: 13, color: C.dim }}
        />
      );
    }
    if (clearable && !readOnly && value) {
      return (
        <TextInput.Icon
          icon="close"
          onPress={() => onChangeText('')}
          accessibilityLabel={`Clear ${label}`}
          color={C.dim}
          size={24}
        />
      );
    }
    return null;
  }, [rightText, clearable, readOnly, value, label, onChangeText]);

  return (
    <TextInput
      label={label}
      value={value}
      onChangeText={handleChange}
      placeholder={placeholder}
      keyboardType={keyboardType as any}
      inputMode={Platform.OS === 'web' ? 'decimal' : undefined}
      autoCorrect={false}
      autoCapitalize="none"
      mode={mode}
      disabled={disabled}
      editable={!readOnly && !disabled}
      selectTextOnFocus={!readOnly}
      right={right}
      onSubmitEditing={handleSubmit}
      returnKeyType={handleSubmit ? 'done' : undefined}
      // Token-aligned colours
      style={{ backgroundColor: C.panel, borderRadius: R.field }}
      textColor={C.ink}
      placeholderTextColor={C.dim}
      outlineColor={C.edge}
      activeOutlineColor={C.accent}
      outlineStyle={{ borderRadius: R.field }}
      theme={{
        colors: {
          background: C.panel,
          onSurface: C.ink,
          onSurfaceVariant: C.ink2,
          placeholder: C.dim,
        },
        fonts: {
          bodyLarge: { fontFamily: FONT.mono, fontWeight: '400' as const },
          labelLarge: { fontFamily: FONT.sans, fontWeight: '400' as const },
        },
      }}
    />
  );
}
